import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
    Body1,
    Box,
    Stack,
    TextButton,
} from '@myphx-engineering/phx-design-system';

import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { CoverageStatus } from '../../../../graphql/generated/graphql';
import { useFlags } from '../../../../hooks/use-flags';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

type CoverageNoticeTextProps = {
    coverage: CoverageStatus;
    govtSelfPayOnly: boolean;
};

const CoverageNoticeText = ({
    coverage,
    govtSelfPayOnly,
}: CoverageNoticeTextProps) => {
    const { t } = useTranslation('lilly');

    let bodyText: ReactNode = '';

    if (coverage === CoverageStatus.NotCovered) {
        bodyText = (
            <Trans
                t={t}
                i18nKey="fill.coverage.notCovered"
                components={{ 1: <strong /> }}
            />
        );
    }

    if (coverage === CoverageStatus.Unknown) {
        bodyText = govtSelfPayOnly ? (
            <Trans
                t={t}
                i18nKey="fill.coverage.unknownCommercial"
                components={{ 1: <strong /> }}
            />
        ) : (
            <Trans
                t={t}
                i18nKey="fill.coverage.unknown"
                components={{ 1: <strong /> }}
            />
        );
    }

    if (!bodyText) {
        return null;
    }

    return <Body1>{bodyText}</Body1>;
};

type CoverageNoticeButtonProps = CoverageNoticeTextProps & {
    drxId: string;
    query: string;
};

const CoverageNoticeButton = ({
    coverage,
    drxId,
    query,
    govtSelfPayOnly,
}: CoverageNoticeButtonProps) => {
    const { t } = useTranslation('lilly');

    let linkText: ReactNode = '';

    if (coverage === CoverageStatus.NotCovered) {
        linkText = t('fill.selfPay.coverage.thinkCovered');
    }

    if (coverage === CoverageStatus.Unknown) {
        linkText = govtSelfPayOnly
            ? t('fill.selfPay.coverage.thinkInsuranceCommercial')
            : t('fill.selfPay.coverage.thinkInsurance');
    }

    if (!linkText) {
        return null;
    }

    return (
        <Box>
            <TextButton
                link
                to={{
                    pathname: getAbsoluteRoute('fill.drxId.pathSelect', {
                        drxId,
                    }),
                    search: query ? `?${query}` : '',
                }}
            >
                {linkText}
            </TextButton>
        </Box>
    );
};

export const SelfPayCoverageNotice = () => {
    const { t } = useTranslation('lilly');
    const { coverage, drxId, medication } = useFillPrescription();
    const [params] = useSearchParams();
    const { disableSelfPayCoverageNotice } = useFlags();

    // If the copay assistance card specifically requires commercial insurance, then patients with Government insurance (e.g. Medicaid) _must_ use self-pay
    const insuranceTerms = useProgramTerms(medication, 'insurance');
    const govtSelfPayOnly =
        insuranceTerms?.savingsCard?.insuranceRequirement === 'ANY_COMMERCIAL';

    const query = params.toString();

    if (
        disableSelfPayCoverageNotice ||
        (coverage !== CoverageStatus.NotCovered &&
            coverage !== CoverageStatus.Unknown)
    ) {
        return null;
    }

    return (
        <Stack gap="xs">
            <CoverageNoticeText
                coverage={coverage}
                govtSelfPayOnly={govtSelfPayOnly}
            />
            <CoverageNoticeButton
                coverage={coverage}
                govtSelfPayOnly={govtSelfPayOnly}
                drxId={drxId}
                query={query}
            />
            {govtSelfPayOnly ? (
                <Body1>
                    <Trans
                        t={t}
                        i18nKey="fill.selfPay.coverage.govtSelfPayOnly"
                        components={{ 1: <strong /> }}
                    />
                </Body1>
            ) : null}
        </Stack>
    );
};
